<template>
  <v-row>
    <v-col
      cols="12"
      class="d-flex flex-column flex-sm-row justify-sm-space-between gap-2">
      <div class="d-flex flex-column flex-sm-row align-sm-center gap-2">
        <h1 class="title primary--text mr-4">
          เลือกสินค้าที่ต้องการพิมพ์บาร์โค้ด
        </h1>
        <BarcodeFilter
          :value="filter"
          @onFilter="handleFilter($event)" />
      </div>
      <div class="d-flex flex-column flex-sm-row align-sm-center gap-2">
        <v-text-field
          :value="searchText"
          placeholder="ค้นหาสินค้า"
          append-icon="mdi-magnify"
          outlined
          hide-details
          dense
          @input="setSearchTextDebounce($event)" />
        <div class="d-flex">
          <snt-export
            :headers="strHeaders"
            :callback="getExport"
            extract>
            <template #activator="{ attrs, submitting, disabled, on }">
              <v-btn
                v-bind="attrs"
                class="mb-sm-0 ml-sm-3"
                color="primary"
                height="40"
                :loading="submitting"
                :disabled="disabled"
                :ripple="false"
                rounded
                elevation="0"
                v-on="on">
                Export File
              </v-btn>
            </template>
          </snt-export>
          <div class="mr-3" />
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-icon
                color="primary"
                v-bind="attrs"
                v-on="on">
                mdi-information-outline
              </v-icon>
            </template>
            <p>โปรแกรมที่แนะนำ</p>
            <p class="mb-0">
              1. GoLabel (สำหรับปรินท์เตอร์ GoDex)
            </p>
            <p class="mb-0">
              2. Label4J (สำหรับเครื่องพิมพ์ A4 Sticker)
            </p>
            <p class="mb-0">
              3. Bartender (สำหรับเครื่องพิมพ์ Thermal)
            </p>
          </v-tooltip>
        </div>
      </div>
    </v-col>
    <v-col
      v-if="productList"
      cols="12">
      <ProductTableBarcode
        v-model="selected"
        :headers="headers"
        :items="productList.nodes"
        :total-pages="productList.pagesCount"
        :loading="$apollo.queries.productList.loading"
        :page="page"
        :per-page="perPage"
        printable
        @onPageChange="handlePageChange($event)" />
    </v-col>
    <v-col
      cols="12"
      class="d-flex justify-end">
      <BarcodeSetting
        :items="itemsForPrint"
        :disabled="$_.isEmpty(itemsForPrint)" />
    </v-col>
  </v-row>
</template>

<script>
import { GET_PRODUCT_LIST_FOR_PRINT_BARCODE } from '@/resources/graphql'
import SntExport from '@shipnity/components/src/components/SntExport/SntExport.vue'
import BarcodeFilter from '../components/BarcodeFilter.vue'
import BarcodeSetting from '../components/BarcodeSetting.vue'
import ProductTableBarcode from '../components/ProductTableBarcode.vue'

export default {
  components: {
    BarcodeFilter,
    BarcodeSetting,
    ProductTableBarcode,
    SntExport
  },
  apollo: {
    productList: {
      fetchPolicy: 'network-only',
      query: GET_PRODUCT_LIST_FOR_PRINT_BARCODE,
      variables () {
        const filter = {}
        if (this.filter?.shopee) {
          filter.shopee = this.filter?.shopee
        }
        if (this.filter?.lazada) {
          filter.lazada = this.filter?.lazada
        }
        return {
          page: this.page,
          perPage: this.perPage,
          categoryId: this.filter?.categoryId || '',
          brandId: this.filter?.brandId || '',
          stockId: this.filter?.stockId || '',
          search: this.searchText,
          ...filter
        }
      }
    }
  },
  data () {
    return {
      headers: [
        {
          text: 'รหัสสินค้า',
          align: 'start',
          sortable: false,
          value: 'code',
          width: '250px'
        },
        {
          text: 'ชื่อสินค้า',
          align: 'start',
          sortable: false,
          value: 'name'
        },
        {
          text: 'บาร์โค้ด',
          align: 'start',
          sortable: false,
          value: 'barcode',
          width: '300px'
        }
      ],
      items: [],
      selected: [],
      productList: {
        nodes: [],
        nodesCount: 0,
        pagesCount: 0
      },
      page: 1,
      perPage: 10,
      searchText: '',
      filter: {
        categoryId: [],
        brandId: [],
        stockId: null,
        shopee: false,
        lazada: false
      }
    }
  },
  computed: {
    strHeaders () {
      return this.headers.map(({ text }) => text)
    },
    itemsForPrint () {
      return this.selected.map(({ fullName, price, barcode }) => ({
        name: fullName,
        price: price || 0,
        barcode: barcode || ''
      }))
    }
  },
  created () {
    this.setSearchTextDebounce = this.$_.debounce(this.setSearchText, 800)
  },
  methods: {
    setSearchText (searchText = '') {
      this.searchText = searchText
      this.page = 1
    },
    handlePageChange (page) {
      this.page = page
    },
    handleFilter (filter) {
      this.filter = filter
      this.page = 1
    },
    hasSubproduct (item) {
      return !(item.subproducts.length === 1 && item.subproducts[0].name === 'main')
    },
    async getExport (payload) {
      return this.$apollo
        .query({
          query: GET_PRODUCT_LIST_FOR_PRINT_BARCODE,
          variables: {
            page: payload.page,
            perPage: payload.perPage,
            categoryId: this.filter?.categoryId || '',
            brandId: this.filter?.brandId || '',
            stockId: this.filter?.stockId || '',
            shopee: this.filter?.shopee || undefined,
            lazada: this.filter?.lazada || undefined,
            search: this.searchText
          }
        })
        .then(({ data: { productList } }) => {
          const { nodes, nodesCount, pagesCount } = productList || { nodes: [], pagesCount: 0, nodesCount: 0 }
          const raws = nodes.map((item) => {
            const row = [item.code, item.name, '']
            let subRaws = []
            if (this.hasSubproduct(item)) {
              subRaws = item.subproducts.map((subItem) => {
                const subRow = []
                subRow.push(subItem.code || '')
                subRow.push(subItem.fullName || '')
                subRow.push(subItem.barcode || '')
                return subRow
              })
            } else {
              const main = this.$_.head(item.subproducts)
              row[2] = main?.barcode || ''
            }
            row.push(subRaws)
            return row
          })
          console.log('raws', raws)
          return {
            nodes: raws,
            pagesCount,
            nodesCount
          }
        })
    }
  }
}
</script>
